import { HttpContext } from '@angular/common/http';
import { SKIP_TENANT_URL_REWRITE } from 'src/app/api-url-rewrite.interceptor';
import { TENANT_HEADER_OVERRIDE } from 'src/app/validation.interceptor';

// Shared helper for read methods that accept an optional per-request
// baseUrlOverride (an origin, e.g. "https://server123.vnccy.com" - same
// shape as ApiConfigurationDTO's fields and environment.*BaseURL, no path
// suffix). Used by pages that need to read a SPECIFIC tenant's data from
// that tenant's own assigned server, independent of whichever server the
// logged-in user's own session is routed to via ApiUrlRewriteInterceptor.
//
// Mirrors ApiUrlRewriteInterceptor's own origin-swap logic exactly (same
// trailing-slash trim, same no-op when the override equals the compiled-in
// default) so a page-scoped override behaves identically to the app-wide
// one, just resolved for a different tenant.
export function resolveTenantUrl(defaultUrl: string, baseUrlOverride?: string | null): string {
  if (!baseUrlOverride) {
    return defaultUrl;
  }

  let origin: string;
  try {
    origin = new URL(defaultUrl).origin;
  } catch {
    return defaultUrl;
  }

  const trimmedOverride = baseUrlOverride.replace(/\/+$/, '');
  if (!trimmedOverride || trimmedOverride === origin) {
    return defaultUrl;
  }

  return defaultUrl.replace(origin, trimmedOverride);
}

// Shared HttpOptions builder for every tenant-override read call, replacing
// each service's own copy-pasted `overrideOptions()`. Pairs the two opt-in
// tokens that MUST travel together: SKIP_TENANT_URL_REWRITE (so
// ApiUrlRewriteInterceptor doesn't touch the already-resolved viewed-tenant
// URL) and TENANT_HEADER_OVERRIDE (so ValidationInterceptor sends the
// VIEWED tenant's CompanyId/BranchId instead of the logged-in admin's own -
// without this, the request lands on the right server with the wrong
// tenant's headers and CompanyConfigMiddleware 404s there instead).
// No-ops (returns {}) whenever baseUrlOverride is falsy, so every existing
// caller that omits it is unaffected.
export function tenantOverrideOptions(
  baseUrlOverride: string | undefined | null,
  companyId: string | number,
  branchId: string | number
) {
  if (!baseUrlOverride) {
    return {};
  }
  const context = new HttpContext()
    .set(SKIP_TENANT_URL_REWRITE, true)
    .set(TENANT_HEADER_OVERRIDE, { companyId, branchId });
  return { context };
}
